<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * List item content region for avatar, icon, or main text with leading/trailing positioning.
 *
 * @status internal
 * @group navigation
 * @within hr-list
 * @see https://quasar.dev/vue-components/list-and-list-items Quasar q-item-section API
 */
defineOptions({ name: 'HrItemSection' });

const props = defineProps({
  /**
   * Positions section as the leading element (left side, fixed-width).
   * Use for icons, avatars, and checkboxes.
   */
  avatar: {
    type: Boolean,
    default: false,
  },
  /**
   * Positions section as the trailing element (right side, content-width).
   * Use for trailing icons, badges, and action controls.
   */
  side: {
    type: Boolean,
    default: false,
  },
  /**
   * Aligns the section's content to the top edge of the item.
   * Use with avatar or side on tall multi-line items.
   */
  top: {
    type: Boolean,
    default: false,
  },
  /**
   * Prevents the section from shrinking when sibling sections need space.
   */
  noWrap: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props passed through to q-item-section for backwards compatibility with the legacy
   * theme. Ignored when a design system theme is active.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = {
    avatar: props.avatar,
    side: props.side,
    top: props.top,
    noWrap: props.noWrap,
  };
  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-item-section
    v-bind="effectiveProps"
    :class="hasTheme ? 'hr-item-section' : undefined"
  >
    <!-- @slot default Section content — icon, avatar, label text, or action controls. -->
    <slot />
  </q-item-section>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Leading (avatar) ───────────────────────────────────────────── */
/* Secondary color for leading icons per M3 spec. Falls back to on-surface-variant
   when not inside an HrListGroup container (which sets --hr-on-container). */
.hr-item-section.q-item__section--avatar {
  color: var(--hr-on-container, var(--app-on-surface-variant));
}

/* ── Trailing (side) ────────────────────────────────────────────── */
.hr-item-section.q-item__section--side {
  color: var(--hr-on-container, var(--app-on-surface-variant));
}

</style>
